import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { Urls } from "../../../Urls";
import { DECRYPTION_KEY_HEX } from "../../../config/env.config";
import { currentRequestTimeStamp } from "../../../Constants";
import Axios from "../../../Axios";
import React from "react";
import CryptoJS from "crypto-js";

export const decryptResponse = (encryptedResponse) => {
  try {
    let hex_key = DECRYPTION_KEY_HEX;
    const key = CryptoJS.enc.Hex.parse(hex_key);
    // Convert the base64 encoded string to a WordArray
    const encryptedDataWithIV = CryptoJS.enc.Base64.parse(encryptedResponse);

    // Extract the IV and the encrypted data
    const iv = CryptoJS.lib.WordArray.create(
      encryptedDataWithIV.words.slice(0, 4)
    );
    const encryptedData = CryptoJS.lib.WordArray.create(
      encryptedDataWithIV.words.slice(4)
    );

    // Decrypt the data
    const decryptedData = CryptoJS.AES.decrypt(
      { ciphertext: encryptedData },
      key,
      { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );

    // Convert the decrypted data to a string
    const decryptedStr = CryptoJS.enc.Utf8.stringify(decryptedData);

    // Parse the JSON string into an object
    return JSON.parse(decryptedStr);
  } catch (error) {
    console.error("Decryption failed:", error);
    return null;
  }
};

export const encryptPayload = (payload) => {
  try {
    // Retrieve the key from environment variables
    let hex_key = DECRYPTION_KEY_HEX;
    const key = CryptoJS.enc.Hex.parse(hex_key);
    // Generate a random Initialization Vector (IV)
    const iv = CryptoJS.lib.WordArray.random(16); // AES uses 16 bytes IV
    // Convert the payload to a string
    const payloadStr = JSON.stringify(payload);
    // Encrypt the payload
    const encryptedData = CryptoJS.AES.encrypt(payloadStr, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    // Combine the IV and the encrypted data
    const ivAndEncryptedData = CryptoJS.lib.WordArray.create([
      ...iv.words,
      ...encryptedData.ciphertext.words,
    ]);
    // Encode the combined result to base64
    const encryptedResponse = CryptoJS.enc.Base64.stringify(ivAndEncryptedData);
    return encryptedResponse;
  } catch (error) {
    console.error("Encryption failed:", error);
    return null;
  }
};

export function AddBundleRatesInLanes(lanes, bundles) {
  // Retrieve discount percentage for a given bundle name
  function getBundleDiscount(bundleName) {
    const bundle = bundles.find((b) => b.name === bundleName);
    return bundle ? bundle.discount_perc : 0;
  }

  // Process lanes to apply bundle discounts
  const lanesWithBundleDiscount = lanes?.map((l) => {
    // Get the transporter rate information from the first transporter rate
    const transporterRates =
      l.transporter_rates && l.transporter_rates.length > 0
        ? l.transporter_rates[0]
        : null;

    // Calculate bundle amount after discount
    function BundleAmount(discount) {
      if (l.status === "closed") {
        return transporterRates?.bundle_amount;
      }

      const rate = !isNaN(
        (
          transporterRates.rate -
          (transporterRates.rate * discount) / 100
        ).toFixed(2)
      )
        ? (
            transporterRates.rate -
            (transporterRates.rate * discount) / 100
          ).toFixed(2)
        : 0;

      return rate;
    }

    if (transporterRates) {
      const discount = getBundleDiscount(transporterRates.bundle);
      return {
        ...l,
        bundle_amount: discount > 0 ? BundleAmount(discount) : 0,
      };
    } else {
      return {
        ...l,
        bundle_amount: 0,
      };
    }
  });

  return lanesWithBundleDiscount;
}

export const RATECARDHEADERS = [
  "Lane ID",
  "Lane No",
  "Origin",
  // "Origin State",
  "Destination",
  // "Destination State",
  "Truck Type",
  "Volume",
];

export async function ValidateExcelData(
  file, // The Excel file to be validated
  headers, // Array of header names to validate against
  callBack, // Function to call if validation passes
  setFile, // Function to clear the file input
  setIsUploading,
  activeRound // Object representing the current round
) {
  // Function to check the file for validation
  async function checkFile(data) {
    // Read the Excel file data into a workbook object
    const workbook = XLSX.read(data, { type: "array" });

    // Get the first sheet from the workbook
    const sheet = workbook.Sheets[workbook.SheetNames[0]];

    // Convert the sheet to JSON format
    const jsonData = XLSX.utils.sheet_to_json(sheet);

    // Default validation message indicating success
    let validationMessage =
      "Excel file is valid. No empty cells found under the headers.";
    let isValid = true; // Flag to track validation status

    // Iterate through each header to validate
    headers.map((headerName) => {
      // Filter rows where the cell under the current header is empty
      const emptyCells = jsonData.filter((row) => !row[headerName]);

      // If there are empty cells, update validation status and message
      if (emptyCells.length > 0) {
        isValid = false;
        validationMessage = `Excel file validation failed. Empty cells found under the header: ${headerName}.`;
      }
    });

    // If validation is successful, call the callback function
    if (isValid) {
      await callBack();
    } else {
      // If validation fails, clear the file input and show a toast message
      document.getElementById("FileInput").value = "";
      setFile(null);
      toast.info(validationMessage);
    }
  }

  if (!file) {
    toast.error("No file uploaded!")
    setIsUploading(false)
    return;}

  if (!activeRound?.name) {
    return toast.info(
      "Round has ended, please submit your rates in the next round!",
      { position: "top-right" }
    );
  }

  const validateFile = file;

  // Create a FileReader to read the file's content
  const fileReader = new FileReader();
  fileReader.onload = (event) => {
    // Convert the file's result to a Uint8Array and pass it to checkFile
    const data = new Uint8Array(event.target.result);
    checkFile(data);
  };

  // Read the content of the file as ArrayBuffer
  fileReader.readAsArrayBuffer(validateFile);
}

export function getActiveRound(rounds = []) {
  const currentDate = new Date();

  //init the rounds
  let activeRound = {};
  let closedRounds = [];

  //check if any round exists where the status is active and the deadline hasn't passed
  const roundsExists = rounds.find((round) => {
    const deadline = new Date(round.deadline);
    return round.round_status === "active" && currentDate < deadline;
  });

  //if exists set as active round
  if (roundsExists) {
    activeRound = roundsExists;
  } else {
    //push to closed rounds if no status is available
    const closedRoundsExists = rounds.filter((round) => {
      const deadline = new Date(round.deadline);
      return !round?.round_status;
    });

    closedRounds = closedRoundsExists;
  }

  //return the round data object
  return { activeRound, closedRounds };
}

export const ChangeLog = (
  action = undefined,
  change_activity = undefined,
  screen = undefined,
  id = undefined,
  userId=undefined,
  name=undefined,
  contact_number=undefined,
  email=undefined
) => {
  const payload = {
    action: action,
    application: "Lorri",
    change_activity: change_activity,
    screen: screen,
    timestamp: currentRequestTimeStamp(),
    user_email: email,
    procurement_id: id,
    transporter_id: userId,
    poc_name: name,
    poc_phone_number: contact_number,
  };
  try {
    Axios.post(`${Urls.planning}change_log`, payload)
      .then((res) => {})
      .catch((error) => {
        console.log(error);
      });
  } catch (error) {
    console.log(error);
  }
};

export const DateFormatter1 = (inputdate) => {
  if (inputdate) {
    var date = new Date(inputdate);
    // timezone
    var datefortimezone = new Date(inputdate);
    var new_date = datefortimezone.toString();
    var dateparts = new_date.split(" ");
    var str1 = dateparts[6];
    var str2 = dateparts[7];
    var str3 = dateparts[8];
    var space = " ";
    var timezone = str1.concat(space, str2, space, str3);
    // timezone
    var year = date.getFullYear();
    var month = date.getMonth() + 1;
    var dt = date.getDate();
    var time = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    var milliseconds = date.getMilliseconds();

    if (dt < 10) {
      dt = "0" + dt;
    }
    if (month < 10) {
      month = "0" + month;
    }
    return (
      <>
        <div>{dt + "-" + month + "-" + year},</div>

        <div>{time + ":" + milliseconds}</div>
      </>
    );
  }
};
